import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of, finalize } from 'rxjs';

import { Log, PaginatedList, WebExceptionLog } from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';
import { Filter, Sort } from '../table-filters.service';
import { VersionCheckerService } from '../version-checker.service';

@Injectable({
  providedIn: 'root',
})
export class LogsService extends BaseService {
  private logs: Array<WebExceptionLog> = [];
  private browserRetentionToken = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private loadersService: LoadersService,
    private versionCheckerService: VersionCheckerService
  ) {
    super();
    this.browserRetentionToken = this.generateGuid();
  }

  saveWebMissingTranslationLog(message = '', stackTrace = ''): Observable<string> {
    const log = this.buildModel(message, stackTrace);

    if (
      this.logs.map((x) => x.message).includes(log.message) &&
      this.logs.map((x) => x.stackTrace).includes(log.stackTrace)
    ) {
      return of(null);
    }
    this.logs.push(log);

    return this.http.post<string>(this.baseUrl + '/logs/missing-translation/web', log);
  }

  saveWebExceptionLog(message = '', stackTrace = ''): Observable<string> {
    const log = this.buildModel(message, stackTrace);

    if (
      this.logs.map((x) => x.message).includes(log.message) &&
      this.logs.map((x) => x.stackTrace).includes(log.stackTrace)
    ) {
      return of(null);
    }
    this.logs.push(log);

    return this.http.post<string>(this.baseUrl + '/logs/exception/web', log);
  }

  saveChatKitExceptionLog(message = '', stackTrace = ''): Observable<string> {
    const log = this.buildModel(message, stackTrace);

    if (
      this.logs.map((x) => x.message).includes(log.message) &&
      this.logs.map((x) => x.stackTrace).includes(log.stackTrace)
    ) {
      return of(null);
    }
    this.logs.push(log);

    return this.http.post<string>(this.baseUrl + '/logs/exception/chatkit', log);
  }

  saveWebInfoLog(message = '', stackTrace = ''): Observable<string> {
    const log = this.buildModel(message, stackTrace);

    if (
      this.logs.map((x) => x.message).includes(log.message) &&
      this.logs.map((x) => x.stackTrace).includes(log.stackTrace)
    ) {
      return of(null);
    }
    this.logs.push(log);

    return this.http.post<string>(this.baseUrl + '/logs/info/web', log);
  }

  getById(id: string, loader: string): Observable<Log> {
    this.loadersService.show(loader);
    return this.http.get<Log>(this.baseUrl + '/logs/' + id).pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getAppLogs(
    loader: string,
    pageIndex = 0,
    pageSize = 50,
    filters: Filter = null,
    sort: Sort = null
  ): Observable<PaginatedList<Log>> {
    this.loadersService.show(loader);

    return this.http
      .get<
        PaginatedList<Log>
      >(this.baseUrl + '/logs', { params: httpParams().paging(pageIndex, pageSize).table(filters, sort).build() })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  private buildModel(message = '', stackTrace = ''): WebExceptionLog {
    const extraData = `browserRetentionToken: ${this.browserRetentionToken},\n
    deviceInfo: ${JSON.stringify(this.deviceService.getDeviceInfo(), null, '\t')},\n
    version: ${this.versionCheckerService.currentVersion},\n`;
    return {
      message,
      url: this.router.url,
      stackTrace: extraData + stackTrace,
    };
  }

  private generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      // tslint:disable-next-line:one-variable-per-declaration
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
